import React, { useState, useEffect } from 'react';
import {
  schedulingService,
  WeeklySchedule,
  ScheduleOverride,
  EnhancedWeeklyScheduleUpdate,
  EnhancedScheduleOverrideData,
} from '../../services/schedulingService';
import { productionLineService, ProductionLine } from '../../services/productionLineService';
import schedulerService, { OptimizationResult } from '../../services/schedulerService';
import inventoryCheckService, {
  InventoryCheckSummary,
  InventoryCheckPreviewProduct,
} from '../../services/inventoryCheckService';
import {
  schedulingRunsService,
  SchedulingRun,
  SchedulingRunWithErrors,
} from '../../services/schedulingRunsService';
import { formatDate } from '../../utils/date';

interface ScheduleTabProps {
  onError: (error: string) => void;
}

const ScheduleTab: React.FC<ScheduleTabProps> = ({ onError }) => {
  // Schedule state
  const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule[]>([]);
  const [scheduleOverrides, setScheduleOverrides] = useState<ScheduleOverride[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Production line state for overrides
  const [productionLines, setProductionLines] = useState<ProductionLine[]>([]);

  const [showEnhancedScheduleEditor, setShowEnhancedScheduleEditor] = useState(false);
  const [editingScheduleDay, setEditingScheduleDay] = useState<WeeklySchedule | null>(null);
  const [enhancedScheduleFormData, setEnhancedScheduleFormData] =
    useState<EnhancedWeeklyScheduleUpdate>({
      worker_hours: 8,
      workers: 12,
      is_active: true,
      start_time: '08:00',
      end_time: '17:00',
      break_start_time: '12:00',
      break_end_time: '13:00',
    });

  const [overrideLineId, setOverrideLineId] = useState<number | null>(null);
  const [showEnhancedOverrideForm, setShowEnhancedOverrideForm] = useState(false);
  const [enhancedOverrideFormData, setEnhancedOverrideFormData] =
    useState<EnhancedScheduleOverrideData>({
      override_date: '',
      end_date: '',
      worker_hours: 0,
      workers: 12,
      reason: '',
      is_active: true,
      production_line_id: null,
      start_time: '08:00',
      end_time: '17:00',
      break_start_time: '12:00',
      break_end_time: '13:00',
    });

  // Scheduling runs state
  const [schedulingRuns, setSchedulingRuns] = useState<SchedulingRun[]>([]);
  const [schedulingRunsLoading, setSchedulingRunsLoading] = useState(false);

  // Scheduler state
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [lastOptimizationResult, setLastOptimizationResult] = useState<OptimizationResult | null>(
    null
  );

  // Inventory check state
  const [inventoryCheckLoading, setInventoryCheckLoading] = useState(false);
  const [inventoryCheckPreview, setInventoryCheckPreview] = useState<
    InventoryCheckPreviewProduct[]
  >([]);
  const [showInventoryCheckPreview, setShowInventoryCheckPreview] = useState(false);
  const [_lastInventoryCheckResult, setLastInventoryCheckResult] =
    useState<InventoryCheckSummary | null>(null);

  // Scheduling run details modal state
  const [showRunDetailsModal, setShowRunDetailsModal] = useState(false);
  const [selectedRunDetails, setSelectedRunDetails] = useState<SchedulingRunWithErrors | null>(
    null
  );
  const [runDetailsLoading, setRunDetailsLoading] = useState(false);

  useEffect(() => {
    loadScheduleData();
    loadSchedulingRuns();
    loadProductionLines();
  }, []);

  const loadProductionLines = async () => {
    try {
      const lines = await productionLineService.getActiveProductionLines();
      setProductionLines(
        lines.sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name))
      );
    } catch (e) {
      console.error('Error loading production lines:', e);
    }
  };

  const loadScheduleData = async () => {
    try {
      setScheduleLoading(true);
      onError('');
      const [weeklyData, overridesData] = await Promise.all([
        schedulingService.getWeeklySchedule(),
        schedulingService.getScheduleOverrides(),
      ]);
      setWeeklySchedule(weeklyData);
      setScheduleOverrides(overridesData);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setScheduleLoading(false);
    }
  };

  const loadSchedulingRuns = async () => {
    try {
      setSchedulingRunsLoading(true);
      const runs = await schedulingRunsService.getLastSchedulingRuns(5);
      setSchedulingRuns(runs);
    } catch (e) {
      console.error('Error loading scheduling runs:', e);
    } finally {
      setSchedulingRunsLoading(false);
    }
  };

  const handleViewRunDetails = async (runId: number) => {
    try {
      setRunDetailsLoading(true);
      const runDetails = await schedulingRunsService.getSchedulingRunWithErrors(runId);
      setSelectedRunDetails(runDetails);
      setShowRunDetailsModal(true);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setRunDetailsLoading(false);
    }
  };

  const handleUpdateWeeklySchedule = async (
    dayOfWeek: number,
    workerHours: number,
    workers: number,
    isActive: boolean
  ) => {
    try {
      onError('');
      const current = weeklySchedule.find((s) => s.day_of_week === dayOfWeek);
      const data: EnhancedWeeklyScheduleUpdate = {
        worker_hours: workerHours,
        workers,
        is_active: isActive,
        start_time: current?.start_time
          ? schedulingService.formatTimeFromApi(current.start_time)
          : '08:00',
        end_time: current?.end_time
          ? schedulingService.formatTimeFromApi(current.end_time)
          : '17:00',
        break_start_time: current?.break_start_time
          ? schedulingService.formatTimeFromApi(current.break_start_time)
          : '12:00',
        break_end_time: current?.break_end_time
          ? schedulingService.formatTimeFromApi(current.break_end_time)
          : '13:00',
      };
      await schedulingService.updateEnhancedWeeklySchedule(dayOfWeek, data);
      await loadScheduleData();
    } catch (e) {
      onError((e as Error).message);
    }
  };

  const resetEnhancedScheduleForm = () =>
    setEnhancedScheduleFormData({
      worker_hours: 8,
      workers: 12,
      is_active: true,
      start_time: '08:00',
      end_time: '17:00',
      break_start_time: '12:00',
      break_end_time: '13:00',
    });

  const openEnhancedScheduleEditor = (s: WeeklySchedule) => {
    setEditingScheduleDay(s);
    setEnhancedScheduleFormData({
      worker_hours: s.worker_hours,
      workers: s.workers,
      is_active: s.is_active,
      start_time: s.start_time ? schedulingService.formatTimeFromApi(s.start_time) : '08:00',
      end_time: s.end_time ? schedulingService.formatTimeFromApi(s.end_time) : '17:00',
      break_start_time: s.break_start_time
        ? schedulingService.formatTimeFromApi(s.break_start_time)
        : '12:00',
      break_end_time: s.break_end_time
        ? schedulingService.formatTimeFromApi(s.break_end_time)
        : '13:00',
    });
    setShowEnhancedScheduleEditor(true);
  };

  const handleUpdateEnhancedSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingScheduleDay) return;
    try {
      onError('');
      const validation = schedulingService.validateWorkingHours(
        enhancedScheduleFormData.start_time,
        enhancedScheduleFormData.end_time,
        enhancedScheduleFormData.break_start_time,
        enhancedScheduleFormData.break_end_time
      );
      if (!validation.isValid) {
        onError(validation.errors.join(', '));
        return;
      }
      const effective = schedulingService.calculateEffectiveHours(
        enhancedScheduleFormData.start_time,
        enhancedScheduleFormData.end_time,
        enhancedScheduleFormData.break_start_time,
        enhancedScheduleFormData.break_end_time
      );
      const dataWithHours: EnhancedWeeklyScheduleUpdate = {
        ...enhancedScheduleFormData,
        worker_hours: effective,
      };
      await schedulingService.updateEnhancedWeeklySchedule(
        editingScheduleDay.day_of_week,
        dataWithHours
      );
      setShowEnhancedScheduleEditor(false);
      setEditingScheduleDay(null);
      resetEnhancedScheduleForm();
      await loadScheduleData();
    } catch (e) {
      onError((e as Error).message);
    }
  };

  // Overrides
  const resetEnhancedOverrideForm = () =>
    setEnhancedOverrideFormData({
      override_date: '',
      end_date: '',
      worker_hours: 0,
      workers: 12,
      reason: '',
      is_active: true,
      production_line_id: null,
      start_time: '08:00',
      end_time: '17:00',
      break_start_time: '12:00',
      break_end_time: '13:00',
    });

  const handleCreateEnhancedOverride = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      onError('');

      // Validate reason is always required
      if (!enhancedOverrideFormData.reason || enhancedOverrideFormData.reason.trim() === '') {
        onError('Reason is required');
        return;
      }

      const isActive = enhancedOverrideFormData.is_active !== false; // Default to true

      // If is_active is true, validate time fields
      if (isActive) {
        const validation = schedulingService.validateWorkingHours(
          enhancedOverrideFormData.start_time,
          enhancedOverrideFormData.end_time,
          enhancedOverrideFormData.break_start_time,
          enhancedOverrideFormData.break_end_time
        );
        if (!validation.isValid) {
          onError(validation.errors.join(', '));
          return;
        }
        if (!enhancedOverrideFormData.workers || enhancedOverrideFormData.workers < 1) {
          onError('Workers is required when is_active is true');
          return;
        }
      }

      const effectiveHours = isActive
        ? schedulingService.calculateEffectiveHours(
            enhancedOverrideFormData.start_time,
            enhancedOverrideFormData.end_time,
            enhancedOverrideFormData.break_start_time,
            enhancedOverrideFormData.break_end_time
          )
        : 0;

      const start = new Date(enhancedOverrideFormData.override_date);
      const end = new Date(
        enhancedOverrideFormData.end_date || enhancedOverrideFormData.override_date
      );
      const dates: string[] = [];
      const cur = new Date(start);
      while (cur <= end) {
        dates.push(cur.toISOString().split('T')[0]);
        cur.setDate(cur.getDate() + 1);
      }

      await Promise.all(
        dates.map((date) =>
          schedulingService.createEnhancedScheduleOverride({
            ...enhancedOverrideFormData,
            override_date: date,
            worker_hours: effectiveHours,
            is_active: isActive,
          })
        )
      );

      alert(
        `Schedule override${dates.length > 1 ? 's' : ''} created successfully for ${dates.length} day${
          dates.length > 1 ? 's' : ''
        }!`
      );
      setShowEnhancedOverrideForm(false);
      resetEnhancedOverrideForm();
      await loadScheduleData();
    } catch (e) {
      onError((e as Error).message);
    }
  };

  const handleDeleteOverride = async (date: string, productionLineId?: number | null) => {
    if (!confirm('Are you sure you want to delete this schedule override?')) return;
    try {
      onError('');
      setScheduleOverrides((prev) =>
        prev.filter((o) => !(o.override_date === date && o.production_line_id === productionLineId))
      );
      await schedulingService.deleteScheduleOverride(date, productionLineId);
      await loadScheduleData();
    } catch (e) {
      await loadScheduleData();
      onError((e as Error).message);
    }
  };

  // Scheduler
  const handleRunSchedulerOptimization = async () => {
    try {
      setSchedulerLoading(true);
      onError('');
      const result = await schedulerService.runOptimization();
      setLastOptimizationResult(result);
      alert(
        `Scheduler optimization completed successfully!\n\nOrders processed: ${result.ordersProcessed}\nOrders scheduled on time: ${result.ordersScheduledOnTime}\nOptimization score: ${result.optimizationScore}%`
      );
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSchedulerLoading(false);
    }
  };

  const getLineName = (lineId?: number | null): string => {
    if (lineId == null) return 'All Lines (Global)';
    const line = productionLines.find((l) => l.id === lineId);
    return line ? line.name : `Line #${lineId}`;
  };

  // Render helpers
  const renderStatusBadge = (className: string, text: string) => {
    const bg =
      className === 'status-success'
        ? '#d4edda'
        : className === 'status-warning'
          ? '#fff3cd'
          : className === 'status-error'
            ? '#f8d7da'
            : '#e2e3e5';
    const fg =
      className === 'status-success'
        ? '#155724'
        : className === 'status-warning'
          ? '#856404'
          : className === 'status-error'
            ? '#721c24'
            : '#6c757d';
    return (
      <span
        style={{
          padding: '0.25rem 0.5rem',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: bg,
          color: fg,
        }}
      >
        {text}
      </span>
    );
  };

  return (
    <>
      {/* Weekly Schedule */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ margin: '0 0 1.5rem 0' }}>Weekly Schedule</h3>
        {scheduleLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading schedule...</div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Working Hours</th>
                  <th>Break Time</th>
                  <th>Workers</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {weeklySchedule.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <strong>{schedulingService.getDayShortName(s.day_of_week)}</strong>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.875rem' }}>
                        {s.start_time && s.end_time ? (
                          <div>
                            <div>
                              <strong>
                                {schedulingService.formatTimeForDisplay(s.start_time)} -{' '}
                                {schedulingService.formatTimeForDisplay(s.end_time)}
                              </strong>
                            </div>
                            <div style={{ color: '#666', fontSize: '0.75rem' }}>
                              {s.worker_hours} effective hours
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="number"
                              min={0}
                              max={24}
                              step={0.5}
                              value={s.worker_hours}
                              onChange={(e) =>
                                handleUpdateWeeklySchedule(
                                  s.day_of_week,
                                  parseFloat(e.target.value),
                                  s.workers,
                                  s.is_active
                                )
                              }
                              style={{
                                width: 80,
                                padding: '0.25rem 0.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: 4,
                                fontSize: '0.875rem',
                              }}
                            />
                            <span style={{ marginLeft: '0.5rem', color: '#666' }}>hours</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.875rem' }}>
                        {!s.is_active ? (
                          <span style={{ color: '#999', fontSize: '0.75rem', fontStyle: 'italic' }}>
                            Day inactive
                          </span>
                        ) : s.break_start_time && s.break_end_time ? (
                          <div>
                            <strong>
                              {schedulingService.formatTimeForDisplay(s.break_start_time)} -{' '}
                              {schedulingService.formatTimeForDisplay(s.break_end_time)}
                            </strong>
                          </div>
                        ) : (
                          <span style={{ color: '#666', fontSize: '0.75rem' }}>Not configured</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="number"
                          min={1}
                          max={50}
                          step={1}
                          value={s.workers}
                          onChange={(e) =>
                            handleUpdateWeeklySchedule(
                              s.day_of_week,
                              s.worker_hours,
                              parseInt(e.target.value, 10),
                              s.is_active
                            )
                          }
                          style={{
                            width: 80,
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: 4,
                            fontSize: '0.875rem',
                          }}
                        />
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
                          workers
                        </span>
                      </div>
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: s.is_active ? '#28a745' : '#6c757d',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: 4,
                          fontSize: '0.75rem',
                        }}
                      >
                        {s.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-small"
                          onClick={() => openEnhancedScheduleEditor(s)}
                        >
                          Configure Times
                        </button>
                        <button
                          className="btn btn-small"
                          onClick={() =>
                            handleUpdateWeeklySchedule(
                              s.day_of_week,
                              s.worker_hours,
                              s.workers,
                              !s.is_active
                            )
                          }
                        >
                          {s.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Schedule Overrides */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            flexWrap: 'wrap',
            gap: '0.75rem',
          }}
        >
          <h3 style={{ margin: 0 }}>Schedule Overrides</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <select
              value={overrideLineId ?? 'all'}
              onChange={(e) =>
                setOverrideLineId(e.target.value === 'all' ? null : parseInt(e.target.value, 10))
              }
              style={{
                padding: '0.4rem 0.6rem',
                fontSize: '0.85rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
              }}
            >
              <option value="all">All Lines</option>
              {productionLines.map((line) => (
                <option key={line.id} value={line.id}>
                  {line.name}
                </option>
              ))}
            </select>
            <button className="btn btn-primary" onClick={() => setShowEnhancedOverrideForm(true)}>
              Add Override
            </button>
          </div>
        </div>

        {/* Overrides table */}
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Line</th>
                <th>Working Hours</th>
                <th>Break Time</th>
                <th>Workers</th>
                <th>Status</th>
                <th>Reason</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const filtered =
                  overrideLineId == null
                    ? scheduleOverrides
                    : scheduleOverrides.filter(
                        (o) =>
                          o.production_line_id === overrideLineId || o.production_line_id == null
                      );
                return filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>
                      No schedule overrides found
                    </td>
                  </tr>
                ) : (
                  filtered.map((o) => (
                    <tr
                      key={o.id}
                      style={{
                        opacity: o.is_active === false ? 0.6 : 1,
                        backgroundColor: o.is_active === false ? '#f5f5f5' : 'transparent',
                      }}
                    >
                      <td>
                        <strong>{schedulingService.formatDate(o.override_date)}</strong>
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: '0.8rem',
                            padding: '0.15rem 0.4rem',
                            borderRadius: 4,
                            backgroundColor: o.production_line_id == null ? '#e2e3e5' : '#d1ecf1',
                            color: o.production_line_id == null ? '#6c757d' : '#0c5460',
                          }}
                        >
                          {getLineName(o.production_line_id)}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.875rem' }}>
                          {o.start_time && o.end_time ? (
                            <div>
                              <div>
                                <strong>
                                  {schedulingService.formatTimeForDisplay(o.start_time)} -{' '}
                                  {schedulingService.formatTimeForDisplay(o.end_time)}
                                </strong>
                              </div>
                              <div style={{ color: '#666', fontSize: '0.75rem' }}>
                                {o.worker_hours} effective hours
                              </div>
                            </div>
                          ) : (
                            <div>{o.worker_hours} hours</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.875rem' }}>
                          {o.break_start_time && o.break_end_time ? (
                            <div>
                              <strong>
                                {schedulingService.formatTimeForDisplay(o.break_start_time)} -{' '}
                                {schedulingService.formatTimeForDisplay(o.break_end_time)}
                              </strong>
                            </div>
                          ) : (
                            <span style={{ color: '#666', fontSize: '0.75rem' }}>
                              Not configured
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{o.is_active === false ? 'N/A' : `${o.workers} workers`}</td>
                      <td>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: o.is_active === false ? '#6c757d' : '#28a745',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: 4,
                            fontSize: '0.75rem',
                          }}
                        >
                          {o.is_active === false ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td>{o.reason}</td>
                      <td>{schedulingService.formatDateTime(o.created_at)}</td>
                      <td>
                        <button
                          className="btn btn-small"
                          onClick={() =>
                            handleDeleteOverride(o.override_date, o.production_line_id)
                          }
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Scheduling Runs */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ margin: '0 0 1.5rem 0' }}>Recent Scheduling Runs</h3>
        {schedulingRunsLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading scheduling runs...</div>
        ) : schedulingRuns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
            No scheduling runs found
          </div>
        ) : (
          <div className="table-container">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    Run Type
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    Orders
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    Duration
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {schedulingRuns.map((run) => {
                  const statusInfo = schedulingRunsService.formatStatus(run.status);
                  return (
                    <tr key={run.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '0.75rem' }}>
                        {schedulingRunsService.formatRunType(run.run_type)}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {renderStatusBadge(statusInfo.className, statusInfo.text)}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <div style={{ fontSize: '0.875rem' }}>
                          <div>
                            <strong>{run.scheduled_orders}</strong> / {run.total_orders} scheduled
                          </div>
                          {run.failed_orders > 0 && (
                            <div style={{ color: '#dc3545', fontSize: '0.75rem' }}>
                              {run.failed_orders} failed
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {schedulingRunsService.formatDuration(run.run_duration_ms)}
                      </td>
                      <td style={{ padding: '0.75rem' }}>{formatDate(run.created_at)}</td>
                      <td style={{ padding: '0.75rem' }}>
                        <button
                          onClick={() => handleViewRunDetails(run.id)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Auto Scheduler */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0' }}>Auto Scheduler</h3>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>
              Optimize production order scheduling based on due dates, capacity, and material
              grouping
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-primary"
              onClick={handleRunSchedulerOptimization}
              disabled={schedulerLoading}
              style={{ padding: '0.5rem 1rem' }}
            >
              {schedulerLoading ? 'Running…' : 'Run Optimization'}
            </button>
          </div>
        </div>

        {lastOptimizationResult && (
          <div
            style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: 6,
              padding: '1rem',
            }}
          >
            <h4 style={{ marginTop: 0 }}>Last Result</h4>
            <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
              <li>Orders processed: {lastOptimizationResult.ordersProcessed}</li>
              <li>On-time: {lastOptimizationResult.ordersScheduledOnTime}</li>
              <li>Score: {lastOptimizationResult.optimizationScore}%</li>
            </ul>
          </div>
        )}
      </div>

      {/* Inventory Check */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0 }}>Inventory Check</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-outline"
              onClick={async () => {
                try {
                  setInventoryCheckLoading(true);
                  onError('');
                  const preview = await inventoryCheckService.getInventoryCheckPreview();
                  setInventoryCheckPreview(preview);
                  setShowInventoryCheckPreview(true);
                } catch (e) {
                  onError((e as Error).message);
                } finally {
                  setInventoryCheckLoading(false);
                }
              }}
              disabled={inventoryCheckLoading}
            >
              {inventoryCheckLoading ? 'Loading…' : 'Preview'}
            </button>

            <button
              className="btn btn-primary"
              onClick={async () => {
                try {
                  setInventoryCheckLoading(true);
                  onError('');
                  const result = await inventoryCheckService.runInventoryCheck();
                  setLastInventoryCheckResult(result);
                  setShowInventoryCheckPreview(false);
                  setInventoryCheckPreview([]);
                  alert(
                    `Inventory check completed successfully!\n\nProducts checked: ${result.totalProductsChecked}\nProducts below minimum: ${result.productsBelowMinimum}\nProduction orders created: ${result.productionOrdersCreated}\nTotal cases to produce: ${result.totalCasesToProduce}`
                  );
                } catch (e) {
                  onError((e as Error).message);
                } finally {
                  setInventoryCheckLoading(false);
                }
              }}
              disabled={inventoryCheckLoading}
            >
              {inventoryCheckLoading ? 'Running…' : 'Run'}
            </button>
          </div>
        </div>

        {showInventoryCheckPreview && (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Current</th>
                  <th>Minimum</th>
                  <th>Deficit</th>
                </tr>
              </thead>
              <tbody>
                {inventoryCheckPreview.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                      No products below minimum
                    </td>
                  </tr>
                ) : (
                  inventoryCheckPreview.map((p) => (
                    <tr key={p.product_id}>
                      <td>{p.product_name}</td>
                      <td>{p.current_inventory}</td>
                      <td>{p.threshold_minimum_cases}</td>
                      <td>{(p.threshold_minimum_cases || 0) - (p.current_inventory || 0)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals (editor forms) */}
      {showEnhancedScheduleEditor && editingScheduleDay && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Configure Times - {editingScheduleDay.day_name}</h3>
              <button
                className="btn btn-small"
                onClick={() => {
                  setShowEnhancedScheduleEditor(false);
                  setEditingScheduleDay(null);
                  resetEnhancedScheduleForm();
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateEnhancedSchedule}>
              <div style={{ padding: '1.5rem' }}>
                <div className="form-group">
                  <label htmlFor="workers">Workers *</label>
                  <input
                    type="number"
                    id="workers"
                    value={enhancedScheduleFormData.workers}
                    onChange={(e) =>
                      setEnhancedScheduleFormData({
                        ...enhancedScheduleFormData,
                        workers: parseInt(e.target.value, 10),
                      })
                    }
                    required
                    min={1}
                    max={50}
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="start_time">Start Time *</label>
                  <input
                    type="time"
                    id="start_time"
                    value={enhancedScheduleFormData.start_time}
                    onChange={(e) =>
                      setEnhancedScheduleFormData({
                        ...enhancedScheduleFormData,
                        start_time: e.target.value,
                      })
                    }
                    required
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="end_time">End Time *</label>
                  <input
                    type="time"
                    id="end_time"
                    value={enhancedScheduleFormData.end_time}
                    onChange={(e) =>
                      setEnhancedScheduleFormData({
                        ...enhancedScheduleFormData,
                        end_time: e.target.value,
                      })
                    }
                    required
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="break_start_time">Break Start Time *</label>
                  <input
                    type="time"
                    id="break_start_time"
                    value={enhancedScheduleFormData.break_start_time}
                    onChange={(e) =>
                      setEnhancedScheduleFormData({
                        ...enhancedScheduleFormData,
                        break_start_time: e.target.value,
                      })
                    }
                    required
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="break_end_time">Break End Time *</label>
                  <input
                    type="time"
                    id="break_end_time"
                    value={enhancedScheduleFormData.break_end_time}
                    onChange={(e) =>
                      setEnhancedScheduleFormData({
                        ...enhancedScheduleFormData,
                        break_end_time: e.target.value,
                      })
                    }
                    required
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={enhancedScheduleFormData.is_active}
                      onChange={(e) =>
                        setEnhancedScheduleFormData({
                          ...enhancedScheduleFormData,
                          is_active: e.target.checked,
                        })
                      }
                      style={{ marginRight: '0.5rem' }}
                    />
                    Day is Active
                  </label>
                </div>
              </div>

              <div
                style={{
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.5rem',
                }}
              >
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowEnhancedScheduleEditor(false);
                    setEditingScheduleDay(null);
                    resetEnhancedScheduleForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEnhancedOverrideForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Add Schedule Override</h3>
              <button
                className="btn btn-small"
                onClick={() => {
                  setShowEnhancedOverrideForm(false);
                  resetEnhancedOverrideForm();
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateEnhancedOverride}>
              <div style={{ padding: '1.5rem' }}>
                <div className="form-group">
                  <label htmlFor="override_date">Start Date *</label>
                  <input
                    type="date"
                    id="override_date"
                    value={enhancedOverrideFormData.override_date}
                    onChange={(e) =>
                      setEnhancedOverrideFormData({
                        ...enhancedOverrideFormData,
                        override_date: e.target.value,
                      })
                    }
                    required
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="end_date">End Date (optional)</label>
                  <input
                    type="date"
                    id="end_date"
                    value={enhancedOverrideFormData.end_date}
                    onChange={(e) =>
                      setEnhancedOverrideFormData({
                        ...enhancedOverrideFormData,
                        end_date: e.target.value,
                      })
                    }
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '0.75rem' }}>
                    Leave empty for single day, or set to create overrides for a date range
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="override_line">Production Line</label>
                  <select
                    id="override_line"
                    value={enhancedOverrideFormData.production_line_id ?? ''}
                    onChange={(e) =>
                      setEnhancedOverrideFormData({
                        ...enhancedOverrideFormData,
                        production_line_id:
                          e.target.value === '' ? null : parseInt(e.target.value, 10),
                      })
                    }
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">All Lines (Global)</option>
                    {productionLines.map((line) => (
                      <option key={line.id} value={line.id}>
                        {line.name}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#666', fontSize: '0.75rem' }}>
                    Global overrides apply to all lines. Select a specific line to override only
                    that line.
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={enhancedOverrideFormData.is_active !== false}
                      onChange={(e) =>
                        setEnhancedOverrideFormData({
                          ...enhancedOverrideFormData,
                          is_active: e.target.checked,
                        })
                      }
                      style={{ marginRight: '0.5rem' }}
                    />
                    Is Active
                  </label>
                  <small
                    style={{
                      color: '#666',
                      fontSize: '0.75rem',
                      display: 'block',
                      marginTop: '0.25rem',
                    }}
                  >
                    If unchecked, this day will be marked as non-working (only reason is required)
                  </small>
                </div>

                {enhancedOverrideFormData.is_active !== false && (
                  <>
                    <div className="form-group">
                      <label htmlFor="override_workers">Workers *</label>
                      <input
                        type="number"
                        id="override_workers"
                        value={enhancedOverrideFormData.workers}
                        onChange={(e) =>
                          setEnhancedOverrideFormData({
                            ...enhancedOverrideFormData,
                            workers: parseInt(e.target.value, 10),
                          })
                        }
                        required
                        min={1}
                        max={50}
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="override_start_time">Start Time *</label>
                      <input
                        type="time"
                        id="override_start_time"
                        value={enhancedOverrideFormData.start_time}
                        onChange={(e) =>
                          setEnhancedOverrideFormData({
                            ...enhancedOverrideFormData,
                            start_time: e.target.value,
                          })
                        }
                        required
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="override_end_time">End Time *</label>
                      <input
                        type="time"
                        id="override_end_time"
                        value={enhancedOverrideFormData.end_time}
                        onChange={(e) =>
                          setEnhancedOverrideFormData({
                            ...enhancedOverrideFormData,
                            end_time: e.target.value,
                          })
                        }
                        required
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="override_break_start">Break Start Time *</label>
                      <input
                        type="time"
                        id="override_break_start"
                        value={enhancedOverrideFormData.break_start_time}
                        onChange={(e) =>
                          setEnhancedOverrideFormData({
                            ...enhancedOverrideFormData,
                            break_start_time: e.target.value,
                          })
                        }
                        required
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="override_break_end">Break End Time *</label>
                      <input
                        type="time"
                        id="override_break_end"
                        value={enhancedOverrideFormData.break_end_time}
                        onChange={(e) =>
                          setEnhancedOverrideFormData({
                            ...enhancedOverrideFormData,
                            break_end_time: e.target.value,
                          })
                        }
                        required
                        style={{
                          padding: '0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label htmlFor="override_reason">Reason *</label>
                  <input
                    type="text"
                    id="override_reason"
                    value={enhancedOverrideFormData.reason}
                    onChange={(e) =>
                      setEnhancedOverrideFormData({
                        ...enhancedOverrideFormData,
                        reason: e.target.value,
                      })
                    }
                    placeholder="e.g., Holiday, Special event"
                    required
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.5rem',
                }}
              >
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowEnhancedOverrideForm(false);
                    resetEnhancedOverrideForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Override
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Scheduling Run Details Modal */}
      {showRunDetailsModal && selectedRunDetails && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowRunDetailsModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '800px',
              maxHeight: '80vh',
              overflow: 'auto',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <h3 style={{ margin: 0, color: '#333' }}>Scheduling Run Details</h3>
              <button
                onClick={() => setShowRunDetailsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                }}
              >
                ×
              </button>
            </div>

            {runDetailsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div>Loading run details...</div>
              </div>
            ) : (
              <div>
                {/* Run Summary */}
                <div style={{ marginBottom: '2rem' }}>
                  <h4 style={{ color: '#333', marginBottom: '1rem' }}>Run Summary</h4>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1rem',
                    }}
                  >
                    <div>
                      <strong>Run ID:</strong> {selectedRunDetails.id}
                    </div>
                    <div>
                      <strong>Type:</strong> {selectedRunDetails.run_type}
                    </div>
                    <div>
                      <strong>Status:</strong>
                      <span
                        style={{
                          color:
                            selectedRunDetails.status === 'success'
                              ? '#28a745'
                              : selectedRunDetails.status === 'partial'
                                ? '#ffc107'
                                : '#dc3545',
                          marginLeft: '0.5rem',
                        }}
                      >
                        {selectedRunDetails.status}
                      </span>
                    </div>
                    <div>
                      <strong>Duration:</strong>{' '}
                      {schedulingRunsService.formatDuration(selectedRunDetails.run_duration_ms)}
                    </div>
                    <div>
                      <strong>Total Orders:</strong> {selectedRunDetails.total_orders}
                    </div>
                    <div>
                      <strong>Scheduled:</strong>
                      <span style={{ color: '#28a745', marginLeft: '0.5rem' }}>
                        {selectedRunDetails.scheduled_orders}
                      </span>
                    </div>
                    <div>
                      <strong>Failed:</strong>
                      <span style={{ color: '#dc3545', marginLeft: '0.5rem' }}>
                        {selectedRunDetails.failed_orders}
                      </span>
                    </div>
                    <div>
                      <strong>Created:</strong> {formatDate(selectedRunDetails.created_at)}
                    </div>
                  </div>
                </div>

                {/* Errors Section */}
                {selectedRunDetails.errors && selectedRunDetails.errors.length > 0 && (
                  <div>
                    <h4 style={{ color: '#333', marginBottom: '1rem' }}>
                      Errors ({selectedRunDetails.errors.length})
                    </h4>
                    <div
                      style={{
                        maxHeight: '300px',
                        overflow: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    >
                      {selectedRunDetails.errors.map((error, index) => (
                        <div
                          key={error.id}
                          style={{
                            padding: '1rem',
                            borderBottom:
                              index < selectedRunDetails.errors.length - 1
                                ? '1px solid #eee'
                                : 'none',
                            backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: '0.5rem',
                            }}
                          >
                            <div>
                              <strong style={{ color: '#dc3545' }}>{error.error_type}</strong>
                              {error.order_id && (
                                <span style={{ marginLeft: '1rem', color: '#666' }}>
                                  Order ID: {error.order_id}
                                </span>
                              )}
                              {error.product_id && (
                                <span style={{ marginLeft: '1rem', color: '#666' }}>
                                  Product ID: {error.product_id}
                                </span>
                              )}
                            </div>
                            <small style={{ color: '#666' }}>{formatDate(error.created_at)}</small>
                          </div>
                          <div style={{ color: '#333', fontSize: '0.9rem' }}>
                            {error.error_message}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!selectedRunDetails.errors || selectedRunDetails.errors.length === 0) && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#28a745' }}>
                    <strong>No errors found for this run!</strong>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: '2rem', textAlign: 'right' }}>
              <button
                onClick={() => setShowRunDetailsModal(false)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduleTab;
